/**
 * GABBIANO ROYAL — THEME JAVASCRIPT
 * Vanilla JS only. No jQuery. No Bootstrap. No CDN libraries.
 * Modules: Header | Mobile Nav | Product Gallery | Variant Selection
 *          Quantity Selector | Add to Cart | Sticky ATC | Size Guide Modal
 *          Review Carousel | Email Capture | Scroll Animations | Cart Count
 */

(function () {
  'use strict';

  /* ============================================================
     UTILITIES
  ============================================================ */
  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    function handleKeyDown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    element.addEventListener('keydown', handleKeyDown);
    if (first) first.focus();
    return () => element.removeEventListener('keydown', handleKeyDown);
  }

  /* ============================================================
     HEADER — Scroll state + sticky
  ============================================================ */
  const HeaderModule = {
    init() {
      this.header = $('#SiteHeader');
      if (!this.header) return;
      this.scrollThreshold = 10;
      this.onScroll();
      window.addEventListener('scroll', debounce(() => this.onScroll(), 10), { passive: true });
    },
    onScroll() {
      const scrolled = window.scrollY > this.scrollThreshold;
      this.header.classList.toggle('is-scrolled', scrolled);
    }
  };

  /* ============================================================
     MOBILE NAVIGATION
  ============================================================ */
  const MobileNavModule = {
    init() {
      this.toggle = $('#NavToggle');
      this.nav = $('#MobileNav');
      this.close = $('#NavClose');
      this.overlay = $('#MobileNavOverlay');
      if (!this.toggle || !this.nav) return;

      this.toggle.addEventListener('click', () => this.open());
      if (this.close) this.close.addEventListener('click', () => this.closeNav());
      if (this.overlay) this.overlay.addEventListener('click', () => this.closeNav());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.nav.getAttribute('aria-hidden') === 'false') {
          this.closeNav();
        }
      });
    },

    open() {
      this.nav.setAttribute('aria-hidden', 'false');
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      this.removeTrap = trapFocus(this.nav.querySelector('.mobile-nav__drawer'));
    },

    closeNav() {
      this.nav.setAttribute('aria-hidden', 'true');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (this.removeTrap) this.removeTrap();
      this.toggle.focus();
    }
  };

  /* ============================================================
     PRODUCT GALLERY — Image switching via thumbnails
  ============================================================ */
  const GalleryModule = {
    init() {
      this.mainImage = $('#MainProductImage');
      this.thumbnails = $$('.featured-product__thumbnail');
      if (!this.mainImage || !this.thumbnails.length) return;

      this.thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', () => this.switchImage(thumb));
      });
    },

    switchImage(thumb) {
      const newSrc = thumb.dataset.imageSrc;
      const newAlt = thumb.dataset.imageAlt;
      if (!newSrc) return;

      // Update main image with crossfade
      this.mainImage.style.opacity = '0';
      this.mainImage.style.transition = 'opacity 0.2s ease';

      const img = new Image();
      img.src = newSrc;
      img.onload = () => {
        this.mainImage.src = newSrc;
        this.mainImage.alt = newAlt || '';
        this.mainImage.style.opacity = '1';
      };

      // Update active thumbnail
      this.thumbnails.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-pressed', 'false');
      });
      thumb.classList.add('is-active');
      thumb.setAttribute('aria-pressed', 'true');
    }
  };

  /* ============================================================
     VARIANT SELECTION — Color + Size
  ============================================================ */
  const VariantModule = {
    init() {
      this.form = $('#ProductForm');
      if (!this.form) return;

      this.variantInput = $('#SelectedVariantId', this.form);
      this.priceEl = $('#ProductPrice');
      this.atcBtn = $('#AddToCartBtn');
      this.atcText = $('#AtcText');
      this.selectedColorEl = $('#SelectedColor');
      this.selectedSizeEl = $('#SelectedSize');

      // Color swatches
      $$('.color-swatch', this.form).forEach((swatch) => {
        swatch.addEventListener('click', () => this.selectOption(swatch, 'color'));
      });

      // Size buttons
      $$('.size-btn', this.form).forEach((btn) => {
        btn.addEventListener('click', () => this.selectOption(btn, 'size'));
      });
    },

    selectOption(element, type) {
      const value = element.querySelector('input').value;
      const optionName = element.querySelector('input').name;

      // Update active state
      const group = type === 'color'
        ? $$('.color-swatch', this.form)
        : $$('.size-btn', this.form);

      group.forEach((el) => el.classList.remove('is-selected'));
      element.classList.add('is-selected');

      // Update label
      if (type === 'color' && this.selectedColorEl) this.selectedColorEl.textContent = value;
      if (type === 'size' && this.selectedSizeEl) this.selectedSizeEl.textContent = value;

      // Update variant
      this.updateVariant();
    },

    updateVariant() {
      // Get selected option values from form
      const option1Input = this.form.querySelector('input[name="option1"]:checked');
      const option2Input = this.form.querySelector('input[name="option2"]:checked');

      const option1 = option1Input ? option1Input.value : null;
      const option2 = option2Input ? option2Input.value : null;

      // Update hidden variant ID by matching against window.productVariants
      if (window.__productVariants) {
        const match = window.__productVariants.find((v) => {
          if (option1 && option2) return v.option1 === option1 && v.option2 === option2;
          if (option1) return v.option1 === option1;
          return true;
        });

        if (match) {
          if (this.variantInput) this.variantInput.value = match.id;

          // Update price
          if (this.priceEl) {
            const priceFormatted = this.formatMoney(match.price);
            const priceEl = this.priceEl.querySelector('.featured-product__price');
            if (priceEl) priceEl.textContent = priceFormatted;
          }

          // Update ATC state
          if (this.atcBtn && this.atcText) {
            if (match.available) {
              this.atcBtn.disabled = false;
              this.atcText.textContent = 'Shop the Bamboo Shirt';
            } else {
              this.atcBtn.disabled = true;
              this.atcText.textContent = 'Sold Out';
            }
          }

          // Update sticky ATC price
          const stickyPrice = $('.sticky-atc__price');
          if (stickyPrice) stickyPrice.textContent = this.formatMoney(match.price);

          // Update URL without reload
          const url = new URL(window.location);
          url.searchParams.set('variant', match.id);
          window.history.replaceState({}, '', url.toString());
        }
      }
    },

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  /* ============================================================
     QUANTITY SELECTOR
  ============================================================ */
  const QuantityModule = {
    init() {
      $$('.quantity-selector').forEach((selector) => {
        const input = selector.querySelector('.quantity-selector__input');
        const minus = selector.querySelector('[data-action="minus"]');
        const plus = selector.querySelector('[data-action="plus"]');

        if (!input) return;

        if (minus) {
          minus.addEventListener('click', () => {
            const val = parseInt(input.value, 10);
            if (val > parseInt(input.min || 1, 10)) input.value = val - 1;
          });
        }

        if (plus) {
          plus.addEventListener('click', () => {
            const val = parseInt(input.value, 10);
            const max = parseInt(input.max || 99, 10);
            if (val < max) input.value = val + 1;
          });
        }
      });
    }
  };

  /* ============================================================
     ADD TO CART — AJAX
  ============================================================ */
  const CartModule = {
    init() {
      this.form = $('#ProductForm');
      this.atcBtn = $('#AddToCartBtn');
      this.atcText = $('#AtcText');
      this.spinner = this.atcBtn ? this.atcBtn.querySelector('.featured-product__atc-spinner') : null;
      this.cartCountEl = $('#CartCount');

      if (!this.form) return;

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });

      // Sticky ATC button
      const stickyBtn = $('#StickyAtcBtn');
      if (stickyBtn) {
        stickyBtn.addEventListener('click', () => {
          // Scroll up to main ATC and submit form
          if (this.form) {
            this.addToCart();
          }
        });
      }
    },

    async addToCart() {
      if (!this.form || !this.atcBtn) return;
      if (this.atcBtn.disabled) return;

      const variantId = this.form.querySelector('[name="id"]');
      const quantity = this.form.querySelector('[name="quantity"]');

      if (!variantId || !variantId.value) {
        this.showError('Please select your options');
        return;
      }

      // Loading state
      this.atcBtn.classList.add('is-loading');
      this.atcBtn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: variantId.value,
            quantity: quantity ? parseInt(quantity.value, 10) : 1
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Could not add to cart');
        }

        await response.json();

        // Update cart count
        await this.updateCartCount();

        // Show success state briefly
        if (this.atcText) this.atcText.textContent = '✓ Added to Cart';
        setTimeout(() => {
          if (this.atcText) this.atcText.textContent = 'Shop the Bamboo Shirt';
          this.atcBtn.classList.remove('is-loading');
          this.atcBtn.disabled = false;
        }, 2000);

        // Navigate to cart
        setTimeout(() => {
          window.location.href = '/cart';
        }, 800);

      } catch (err) {
        this.atcBtn.classList.remove('is-loading');
        this.atcBtn.disabled = false;
        this.showError(err.message || 'Something went wrong. Please try again.');
      }
    },

    async updateCartCount() {
      try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await res.json();
        if (this.cartCountEl) {
          this.cartCountEl.textContent = cart.item_count;
          this.cartCountEl.classList.toggle('is-hidden', cart.item_count === 0);
        }
      } catch (e) {
        // Silently fail cart count update
      }
    },

    showError(message) {
      const existing = document.querySelector('.atc-error-message');
      if (existing) existing.remove();

      const el = document.createElement('p');
      el.className = 'atc-error-message';
      el.style.cssText = 'color: #DC2626; font-size: 0.875rem; margin-top: 0.5rem;';
      el.textContent = message;
      this.form.appendChild(el);
      setTimeout(() => el.remove(), 4000);
    }
  };

  /* ============================================================
     STICKY ATC BAR — Appears when main ATC scrolls out of view
  ============================================================ */
  const StickyAtcModule = {
    init() {
      this.stickyBar = $('#StickyAtc');
      this.atcBtn = $('#AddToCartBtn');
      if (!this.stickyBar || !this.atcBtn) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const isVisible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.stickyBar.classList.toggle('is-visible', isVisible);
            this.stickyBar.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
          });
        },
        { threshold: 0, rootMargin: '0px 0px 0px 0px' }
      );

      observer.observe(this.atcBtn);
    }
  };

  /* ============================================================
     SIZE GUIDE MODAL
  ============================================================ */
  const SizeGuideModal = {
    init() {
      this.modal = $('#SizeGuideModal');
      this.openBtn = $('#SizeGuideBtn');
      this.closeBtn = $('#SizeGuideModalClose');
      this.overlay = $('#SizeGuideModalOverlay');
      if (!this.modal) return;

      if (this.openBtn) this.openBtn.addEventListener('click', () => this.open());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.getAttribute('aria-hidden') === 'false') {
          this.close();
        }
      });
    },

    open() {
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.removeTrap = trapFocus(this.modal.querySelector('.size-guide-modal__dialog'));
    },

    close() {
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.removeTrap) this.removeTrap();
      if (this.openBtn) this.openBtn.focus();
    }
  };

  /* ============================================================
     REVIEW CAROUSEL
  ============================================================ */
  const CarouselModule = {
    init() {
      this.track = $('#ReviewTrack');
      this.prev = $('#ReviewPrev');
      this.next = $('#ReviewNext');
      this.dots = $$('.carousel-dot');
      if (!this.track) return;

      this.cards = $$('.review-card', this.track);
      this.currentIndex = 0;
      this.cardWidth = 0;
      this.visibleCount = this.getVisibleCount();
      this.maxIndex = Math.max(0, this.cards.length - this.visibleCount);

      if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.currentIndex - 1));
      if (this.next) this.next.addEventListener('click', () => this.goTo(this.currentIndex + 1));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.index, 10)));
      });

      // Touch/swipe support
      let startX = 0;
      this.track.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.track.addEventListener('touchend', (e) => {
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
          this.goTo(diff > 0 ? this.currentIndex + 1 : this.currentIndex - 1);
        }
      });

      window.addEventListener('resize', debounce(() => {
        this.visibleCount = this.getVisibleCount();
        this.maxIndex = Math.max(0, this.cards.length - this.visibleCount);
        this.goTo(Math.min(this.currentIndex, this.maxIndex));
      }, 200));

      this.updateUI();
    },

    getVisibleCount() {
      const w = window.innerWidth;
      if (w >= 1024) return 4;
      if (w >= 768) return 2;
      return 1;
    },

    goTo(index) {
      this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
      const cardEl = this.cards[0];
      if (!cardEl) return;
      const cardWidth = cardEl.offsetWidth;
      const gap = parseInt(window.getComputedStyle(this.track).gap) || 24;
      this.track.style.transform = `translateX(-${this.currentIndex * (cardWidth + gap)}px)`;
      this.updateUI();
    },

    updateUI() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.currentIndex);
        dot.setAttribute('aria-selected', i === this.currentIndex ? 'true' : 'false');
      });
      if (this.prev) this.prev.disabled = this.currentIndex === 0;
      if (this.next) this.next.disabled = this.currentIndex >= this.maxIndex;
    }
  };

  /* ============================================================
     EMAIL CAPTURE FORM
  ============================================================ */
  const EmailCaptureModule = {
    init() {
      this.form = $('#GuideForm');
      this.successEl = $('#GuideFormSuccess');
      this.errorEl = $('#GuideFormError');
      if (!this.form) return;

      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    },

    async handleSubmit(e) {
      e.preventDefault();
      const emailInput = this.form.querySelector('#GuideEmail');
      if (!emailInput || !this.validateEmail(emailInput.value)) {
        this.showError('Please enter a valid email address.');
        return;
      }

      const submitBtn = this.form.querySelector('.email-capture__submit');
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending...'; }

      try {
        const formData = new FormData(this.form);
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json' }
        });

        if (response.ok || response.redirected) {
          // Show success
          this.form.style.display = 'none';
          if (this.successEl) this.successEl.style.display = 'flex';
        } else {
          throw new Error('Submission failed');
        }
      } catch (err) {
        // Fallback: show success anyway (Shopify customer form redirects)
        this.form.style.display = 'none';
        if (this.successEl) this.successEl.style.display = 'flex';
      }
    },

    validateEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.style.display = 'block';
        setTimeout(() => { this.errorEl.style.display = 'none'; }, 5000);
      }
    }
  };

  /* ============================================================
     SCROLL ANIMATIONS — IntersectionObserver
  ============================================================ */
  const AnimationModule = {
    init() {
      const elements = $$('[data-animate]');
      if (!elements.length) return;

      if (!('IntersectionObserver' in window)) {
        elements.forEach((el) => el.classList.add('is-visible'));
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.05, rootMargin: '0px 0px 0px 0px' }
      );

      elements.forEach((el) => observer.observe(el));
    }
  };

  /* ============================================================
     ACCORDION ACCESSIBILITY
  ============================================================ */
  const AccordionModule = {
    init() {
      $$('.accordion-item').forEach((item) => {
        const summary = item.querySelector('.accordion-item__trigger');
        if (!summary) return;
        summary.addEventListener('click', () => {
          // Close other items (optional: remove for allow-multiple behavior)
          $$('.accordion-item').forEach((other) => {
            if (other !== item) other.removeAttribute('open');
          });
        });
      });
    }
  };

  /* ============================================================
     CART OVERLAY CLOSE
  ============================================================ */
  const CartOverlayModule = {
    init() {
      const overlay = $('#CartOverlay');
      if (overlay) {
        overlay.addEventListener('click', () => {
          overlay.classList.remove('is-active');
        });
      }
    }
  };

  /* ============================================================
     INITIALIZE ALL MODULES
  ============================================================ */
  function init() {
    HeaderModule.init();
    MobileNavModule.init();
    GalleryModule.init();
    VariantModule.init();
    QuantityModule.init();
    CartModule.init();
    StickyAtcModule.init();
    SizeGuideModal.init();
    CarouselModule.init();
    EmailCaptureModule.init();
    AnimationModule.init();
    AccordionModule.init();
    CartOverlayModule.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
